import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faShop } from "@fortawesome/pro-light-svg-icons"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { SUPPORT_REPORTS_ROUTE } from "../../../../../../constants/routes"
import { useMemo, useRef, useState } from "react"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { fetchSupportMessagesSMS, SupportReportsSMSQuery, SupportSMSResponse } from "../smsApi"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { DateTime } from "luxon"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { MRT_ColumnDef, MRT_RowSelectionState, MRT_TableInstance } from "material-react-table"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { StandardChip } from "../../../../../../shared/components/badges/StandardChip"
import { SupportSMS } from "../smsTypes"
import { ReceiverInput } from "../components/ReceiverInput"
import { DetailedPanel } from "../components/DetailPanel"
import { useExportSMS } from "../hooks/useExportSMS"
import { TextBodyInput } from "../components/TextBodyInput"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { type DtDateRange, StandardDateRangeFilter } from "../../../../../../shared/components/table/StandardDateRangeFilter"

function transformData(data: SupportSMSResponse) {
    return data?.sms || []
}

export function MessagesSMSList() {
    const { l10n } = useAppLocalization()

    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const [phoneNumber, setPhoneNumber] = useState("")
    const [textBody, setTextBody] = useState("")
    const { startDate, endDate } = useInitialDateFilters(DateTime.now().minus({ days: 14 }))
    const [dateState, setDateState] = useState<DtDateRange>({
        startDate: startDate,
        endDate: endDate,
    })

    const tableInstanceRef = useRef<MRT_TableInstance<SupportSMS>>(null)

    const supportSMS = useApiQuery(fetchSupportMessagesSMS, {
        queryName: SupportReportsSMSQuery.SMS_FOR_SUPPORT,
        dependencies: {
            phoneNumber,
            text: textBody,
            from: dateState.startDate?.toISODate(),
            to: dateState.endDate?.toISODate(),
        },
        select: transformData,
    })

    const { handleExportRows } = useExportSMS(
        dateState.startDate?.toISODate() ?? DateTime.now().toISODate(),
        dateState.endDate?.toISODate() ?? DateTime.now().toISODate(),
    )

    const { ApiQueryTable, columnFactory } = useApiQueryTable(supportSMS)

    const exportSelectedRows = () => {
        handleExportRows(tableInstanceRef.current?.getSelectedRowModel().rows)
    }

    const columns = useMemo<MRT_ColumnDef<SupportSMS>[]>(
        () => [
            columnFactory.shortNameColumn({
                header: l10n.getString("support-messages-sms-table-header-receiver"),
                accessorKey: "receiver.phoneNumber",
            }),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("support-messages-sms-table-header-created"),
                    accessorKey: "history.created",
                },
                (row) => row.history.created,
            ),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("support-messages-sms-table-header-sent"),
                    accessorKey: "sentTime",
                },
                (row) => row.sentTime,
            ),
            columnFactory.statusColumn({
                header: l10n.getString("support-messages-sms-table-header-status"),
                accessorKey: "status.text",
                Cell: ({ row }) => {
                    return row.original.status.text === "OK" ? (
                        <LocalizedStrict id="support-messages-sms-table-status-sent">
                            <StandardChip variant="success" placement="table">
                                Sent
                            </StandardChip>
                        </LocalizedStrict>
                    ) : (
                        <LocalizedStrict id="support-messages-sms-table-status-not-sent">
                            <StandardChip variant="red" placement="table">
                                Not sent
                            </StandardChip>
                        </LocalizedStrict>
                    )
                },
                filterSelectOptions: [
                    {
                        value: "sent",
                        text: l10n.getString("support-messages-sms-table-status-sent"),
                    },
                    {
                        value: "not sent",
                        text: l10n.getString("support-messages-sms-table-status-not-sent"),
                    },
                ],
            }),
        ],
        [l10n, columnFactory],
    )

    return (
        <>
            <LocalizedStrict id="support-messages-sms-title" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SUPPORT_MESSAGES_SMS}
                    title="SMS"
                    rightElement={
                        <LocalizedStrict id="support-messages-sms-export-button">
                            <StandardButton disabled={Object.keys(rowSelection).length <= 0} onClick={exportSelectedRows}>
                                Export
                            </StandardButton>
                        </LocalizedStrict>
                    }
                    icon={<FontAwesomeIcon icon={faShop} />}
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="support-nav-item-home">
                            <BreadcrumbLink to={SUPPORT_REPORTS_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-nav-item-messages">
                            <Breadcrumb>Messages</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-nav-item-sms">
                            <Breadcrumb>SMS</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <ApiQueryTable
                tableInstanceRef={tableInstanceRef}
                enableExpanding
                columns={columns}
                onRowSelectionChange={setRowSelection}
                getRowId={(originalRow, index) => {
                    if (!originalRow.id) return index.toString()

                    return originalRow.id.toString()
                }}
                state={{
                    rowSelection,
                }}
                initialState={{ density: "spacious" }}
                enableRowSelection
                enableRowActions={false}
                renderDetailPanel={({ row }) => <DetailedPanel data={row.original} />}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <StandardDateRangeFilter value={dateState} onChange={setDateState} />
                        <ReceiverInput value={phoneNumber} onChange={setPhoneNumber} />
                        <TextBodyInput value={textBody} onChange={setTextBody} />
                    </StandardTableFilter>
                )}
                renderToolbarInternalActions={({ table }) => <StandardTableFilter variant="right" table={table} />}
            />
        </>
    )
}
